import { PermissionAction, PermissionSubject } from '@/types';

import { ROLE_POLICIES, RolePolicyOption } from '../RolePolicyTree/rolePolicies';

export interface RoleTemplate {
    key: string;
    label: string;
    description: string;
    policyKeys: string[];
}

const RESOURCE_SUBJECTS = new Set<PermissionSubject>([
    PermissionSubject.FOLDER,
    PermissionSubject.INVESTMENT,
    PermissionSubject.MEMO,
    PermissionSubject.PIPELINE,
    PermissionSubject.DASHBOARD,
    PermissionSubject.REPORT,
    PermissionSubject.LIST,
    PermissionSubject.LIST_VIEW,
    PermissionSubject.TASK,
    PermissionSubject.TEAM,
]);

const keySet = (...policyGroups: RolePolicyOption[][]): string[] =>
    Array.from(new Set(policyGroups.flat().map(({ key }) => key)));

const policiesWhere = (predicate: (policy: RolePolicyOption) => boolean): RolePolicyOption[] =>
    ROLE_POLICIES.filter(predicate);

const workspaceContextPolicies = policiesWhere(
    ({ action, subject }) =>
        action === PermissionAction.READ &&
        (subject === PermissionSubject.WORKSPACE || subject === PermissionSubject.MEMBER),
);

const resourcePoliciesForActions = (...actions: PermissionAction[]) =>
    policiesWhere(({ action, subject }) => RESOURCE_SUBJECTS.has(subject) && actions.includes(action));

const memberAdministrationPolicies = policiesWhere(
    ({ action, subject }) =>
        (subject === PermissionSubject.USER && action === PermissionAction.INVITE) ||
        (subject === PermissionSubject.MEMBER && action === PermissionAction.UPDATE),
);

const analystPolicies = keySet(
    workspaceContextPolicies,
    resourcePoliciesForActions(PermissionAction.READ, PermissionAction.COMMENT),
);

const associatePolicies = keySet(
    workspaceContextPolicies,
    resourcePoliciesForActions(
        PermissionAction.CREATE,
        PermissionAction.READ,
        PermissionAction.UPDATE,
        PermissionAction.COMMENT,
    ),
);

const portfolioManagerPolicies = keySet(
    workspaceContextPolicies,
    resourcePoliciesForActions(
        PermissionAction.CREATE,
        PermissionAction.READ,
        PermissionAction.UPDATE,
        PermissionAction.COMMENT,
        PermissionAction.INVITE,
    ),
);

const directorPolicies = keySet(
    workspaceContextPolicies,
    memberAdministrationPolicies,
    resourcePoliciesForActions(
        PermissionAction.CREATE,
        PermissionAction.READ,
        PermissionAction.UPDATE,
        PermissionAction.COMMENT,
        PermissionAction.INVITE,
        PermissionAction.MANAGE,
    ),
);

const investmentCommitteePolicies = keySet(
    workspaceContextPolicies,
    resourcePoliciesForActions(PermissionAction.READ, PermissionAction.COMMENT),
    policiesWhere(
        ({ action, subject }) =>
            action === PermissionAction.INVITE &&
            [PermissionSubject.MEMO, PermissionSubject.DASHBOARD, PermissionSubject.REPORT].includes(subject),
    ),
);

export const ROLE_TEMPLATES: RoleTemplate[] = [
    {
        key: 'analyst',
        label: 'Analyst',
        description: 'View and comment on workspace research resources without changing their underlying content.',
        policyKeys: analystPolicies,
    },
    {
        key: 'associate',
        label: 'Associate',
        description: 'Create, edit, review, and comment on workspace resources used throughout active research.',
        policyKeys: associatePolicies,
    },
    {
        key: 'portfolio-manager',
        label: 'Portfolio Manager',
        description: 'Own research workflows and share investment resources with workspace collaborators.',
        policyKeys: portfolioManagerPolicies,
    },
    {
        key: 'director',
        label: 'Director',
        description:
            'Manage workspace resources, collaboration, and member access without changing workspace settings.',
        policyKeys: directorPolicies,
    },
    {
        key: 'cio',
        label: 'CIO',
        description: 'Full access to every available workspace and resource policy represented in this role builder.',
        policyKeys: ROLE_POLICIES.map(({ key }) => key),
    },
    {
        key: 'investment-committee-member',
        label: 'IC Member',
        description: 'Review and comment on research, with sharing access for memos, dashboards, and reports.',
        policyKeys: investmentCommitteePolicies,
    },
];
